import type { Maybe } from '@adornis/base/utilTypes.js';
import {
  isMemberOfOrganization,
  isOrgAdminAnywhere,
  isOrgAdminOfOrganization,
  type MembershipLike,
  organizationIdsWithRole,
  SaraaOrgRole,
} from './db/SaraaUser.js';
import { isPageCheckPayload, type PageCheckable, type PermissionUser } from './policyHelper.js';

/** Result contract: a truthy object decides, `false` abstains and lets the next check run. */
export type CheckResult = { allowed: boolean; reason: string } | false;

const allow = (reason: string) => ({ allowed: true, reason });

/**
 * There is deliberately no `isAdmin` check here any more.
 *
 * A platform admin's grants are global (`checks: []`), so the policy returns
 * allowed before reaching any check. The old sentinel permission and the wrapper
 * that registered `isAdmin` under every action existed only because grants from
 * several roles merged by concatenating their check arrays — a global admin grant
 * unioned with a check-scoped org grant collapsed to check-scoped and silently
 * stripped the admin. `resolveGrantsFor` returns exactly one of two sets, so there
 * is no union and nothing left to work around.
 */

/**
 * Builds the organization-scoped checks for one scope.
 *
 * `getOrganizationId` differs per scope: most entities carry `organizationId`,
 * but for the `organizations` scope the organization *is* the entity, so it is
 * `_id`.
 */
export const organizationChecks = <D extends PageCheckable>(getOrganizationId: (data: D) => Maybe<string>) => ({
  /** The target belongs to an organization this user administers. */
  isOrgAdminOf: async (user: Maybe<PermissionUser>, payload: { data?: D }): Promise<CheckResult> => {
    const data = payload.data;
    if (!data) return false;
    return isOrgAdminOfOrganization(user, getOrganizationId(data))
      ? allow('user is an admin of this organization')
      : false;
  },

  /** The target belongs to an organization this user is a member of, in any role. */
  isOrgMemberOf: async (user: Maybe<PermissionUser>, payload: { data?: D }): Promise<CheckResult> => {
    const data = payload.data;
    if (!data) return false;
    return isMemberOfOrganization(user, getOrganizationId(data))
      ? allow('user is a member of this organization')
      : false;
  },
});

/**
 * Route- and button-visibility check.
 *
 * ERM asks "may this user see this route / this create button" without any entity
 * to check against, so a per-organization answer is impossible there. This answers
 * the coarse question — "could this user ever do this, in any organization" — and
 * the server makes the real per-row decision. It must never be a bare pageCheck
 * pass, or an ORG_USER would see every admin route.
 */
export const isOrgAdminPageCheck = async (
  user: Maybe<PermissionUser>,
  payload: { data?: PageCheckable },
): Promise<CheckResult> => {
  if (!isPageCheckPayload(payload.data)) return false;
  return isOrgAdminAnywhere(user) ? allow('user administers at least one organization') : false;
};

/** As above, but satisfied by membership in any role. */
export const isOrgMemberPageCheck = async (
  user: Maybe<PermissionUser>,
  payload: { data?: PageCheckable },
): Promise<CheckResult> => {
  if (!isPageCheckPayload(payload.data)) return false;
  return (user?.memberships?.length ?? 0) > 0 ? allow('user belongs to at least one organization') : false;
};

export interface TargetUserData extends PageCheckable {
  _id?: Maybe<string>;
  /** Structural, so callers can pass plain data as well as hydrated entities. */
  memberships?: Maybe<MembershipLike[]>;
}

/**
 * `users` scope: the acting user administers at least one organization the target
 * user belongs to. Server-side callers must load the target's `memberships` into
 * the payload — there is nothing to compare against otherwise.
 */
export const isOrgAdminOfTargetUser = async (
  user: Maybe<PermissionUser>,
  payload: { data?: TargetUserData },
): Promise<CheckResult> => {
  const target = payload.data;
  if (!target) return false;
  if (target._id && target._id === user?._id) return allow('user is acting on themselves');

  const myAdminOrgs = organizationIdsWithRole(user, SaraaOrgRole.ORG_ADMIN);
  if (myAdminOrgs.length === 0) return false;

  const targetOrgs = (target.memberships ?? []).flatMap(m => (m.organizationId ? [m.organizationId] : []));
  return targetOrgs.some(orgId => myAdminOrgs.includes(orgId))
    ? allow('target user belongs to an organization this user administers')
    : false;
};

/** The user is acting on their own account. */
export const isSelf = async (user: Maybe<PermissionUser>, payload: { data?: TargetUserData }): Promise<CheckResult> => {
  const target = payload.data;
  if (!target?._id || !user?._id) return false;
  return target._id === user._id ? allow('user is acting on themselves') : false;
};
